import { UnifiedFilter } from './FilterSection';
import { getDateRangeForPreset } from '../FiltersPanel/dateFilterHelpers';

/**
 * Converts unified filters to the format expected by applyFilter
 * This bridges the gap between the unified filter structure and the legacy filter format
 *
 * For dateRange: if a preset is set but from/to are missing (e.g. after loading a saved
 * filter preset from the API), the preset is resolved to actual dates so the filter works.
 *
 * @param unifiedFilters - The unified filter structure
 * @param allFilterKeys - All possible filter keys to ensure all are initialized
 * @param textFilterKeys - Keys that should be strings instead of arrays
 */
export function convertUnifiedFiltersToLegacyFormat(
  unifiedFilters: Record<string, UnifiedFilter>,
  allFilterKeys?: string[],
  textFilterKeys?: string[]
): {
  filters: Record<string, string[] | string>;
  dateRanges: Record<string, { start: Date | null; end: Date | null }>;
  numberRanges: Record<string, { min: number | null; max: number | null }>;
} {
  const filters: Record<string, string[] | string> = {};
  const dateRanges: Record<string, { start: Date | null; end: Date | null }> = {};
  const numberRanges: Record<string, { min: number | null; max: number | null }> = {};

  // Initialize all filter keys with empty arrays/strings to prevent undefined errors
  if (allFilterKeys) {
    allFilterKeys.forEach(key => {
      // Check if this is a text filter
      const isTextFilter = textFilterKeys?.includes(key);
      filters[key] = isTextFilter ? '' : [];
    });
  }

  // Parse date string as UTC to avoid timezone shifts
  const parseDateAsUTC = (dateString: string): Date => {
    if (dateString.includes('T') || dateString.includes('Z')) {
      return new Date(dateString);
    }
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };

  // Now update with actual filter values
  Object.entries(unifiedFilters).forEach(([fieldKey, filter]) => {
    switch (filter.type) {
      case 'multiSelect':
        if (filter.values && filter.values.length > 0) {
          filters[fieldKey] = filter.values;
        } else if (!filters[fieldKey]) {
          filters[fieldKey] = [];
        }
        break;

      case 'dateRange':
        if (filter.preset && filter.preset !== 'all') {
          filters[fieldKey] = [filter.preset];
          let start: Date | null = null;
          let end: Date | null = null;
          if (filter.from || filter.to) {
            start = filter.from ? parseDateAsUTC(filter.from) : null;
            end = filter.to ? parseDateAsUTC(filter.to) : null;
          }
          // Resolve preset to dates when from/to are missing (e.g. saved preset from API)
          if ((start === null && end === null) && filter.preset !== 'custom') {
            const range = getDateRangeForPreset(filter.preset);
            start = range.start;
            end = range.end;
          }
          if (start !== null || end !== null) {
            dateRanges[fieldKey] = { start, end };
          }
        } else if (!filters[fieldKey]) {
          filters[fieldKey] = [];
        }
        break;

      case 'numericRange':
        if (filter.min !== undefined || filter.max !== undefined) {
          numberRanges[fieldKey] = {
            min: filter.min ?? null,
            max: filter.max ?? null,
          };
        }
        // Always initialize as array for numeric filters if not already set
        if (!filters[fieldKey]) {
          filters[fieldKey] = [];
        }
        break;

      case 'boolean':
        if (filter.value !== undefined) {
          // Convert boolean to string representation based on field options
          // For clientIsActive: true -> 'Active', false -> 'Inactive'
          // For isClient: true -> 'Yes', false -> 'No'
          // For isOpen (packages, etc.): true -> 'Open', false -> 'Closed'
          // For todoCompleted (crm-todos): true -> 'Completed', false -> 'Pending'
          // For todoRecurring (crm-todos): true -> 'Recurring', false -> 'One-time'
          // For clientCallSendEmail (crm-calls): true -> 'Yes', false -> 'No'
          // For clientReminderIsDone (reminders): true -> 'Done', false -> 'Not Done'
          if (fieldKey === 'clientIsActive') {
            filters[fieldKey] = [filter.value ? 'Active' : 'Inactive'];
          } else if (fieldKey === 'isClient') {
            filters[fieldKey] = [filter.value ? 'Yes' : 'No'];
          } else if (fieldKey === 'clientCalls') {
            filters[fieldKey] = [filter.value ? 'Has calls' : 'Does not have calls'];
          } else if (fieldKey === 'isOpen') {
            filters[fieldKey] = [filter.value ? 'Open' : 'Closed'];
          } else if (fieldKey === 'todoCompleted') {
            filters[fieldKey] = [filter.value ? 'Completed' : 'Pending'];
          } else if (fieldKey === 'todoRecurring') {
            filters[fieldKey] = [filter.value ? 'Recurring' : 'One-time'];
          } else if (fieldKey === 'clientCallSendEmail') {
            filters[fieldKey] = [filter.value ? 'Yes' : 'No'];
          } else if (fieldKey === 'clientReminderIsDone') {
            filters[fieldKey] = [filter.value ? 'Done' : 'Not Done'];
          } else if (fieldKey === 'appIsPrivate') {
            filters[fieldKey] = [filter.value ? 'Yes' : 'No'];
          } else if (fieldKey === 'status') {
            // client-invoices: boolean filter with options ['Paid', 'Partial', 'Unpaid'] -> true = Paid, false = Partial
            filters[fieldKey] = [filter.value ? 'Paid' : 'Partial'];
          } else if (fieldKey === 'serviceActive') {
            // client-services: true -> 'Active', false -> 'Inactive'
            filters[fieldKey] = [filter.value ? 'Active' : 'Inactive'];
          } else if (fieldKey === 'isActive') {
            // crm-support-agreements (and similar): true -> 'Active', false -> 'Inactive'
            filters[fieldKey] = [filter.value ? 'Active' : 'Inactive'];
          } else if (fieldKey === 'serviceHasReminder') {
            // client-services: true -> 'Has Reminder', false -> 'No Reminder'
            filters[fieldKey] = [filter.value ? 'Has Reminder' : 'No Reminder'];
          } else if (fieldKey === 'noteArchived') {
            // crm-notes: true -> 'Active', false -> 'Archived'
            filters[fieldKey] = [filter.value ? 'Active' : 'Archived'];
          } else {
            filters[fieldKey] = [filter.value ? 'true' : 'false'];
          }
        } else if (!filters[fieldKey]) {
          filters[fieldKey] = [];
        }
        break;

      case 'text':
        if (filter.textValue && filter.textValue.trim() !== '') {
          filters[fieldKey] = filter.textValue.trim();
        } else if (!filters[fieldKey]) {
          filters[fieldKey] = '';
        }
        break;

      case 'select':
        if (filter.textValue && filter.textValue !== '') {
          filters[fieldKey] = [filter.textValue];
        } else if (!filters[fieldKey]) {
          filters[fieldKey] = [];
        }
        break;
    }
  });

  return { filters, dateRanges, numberRanges };
}

/**
 * Relative date presets that are resolved at apply time (e.g. overdue, thisMonth).
 * We do not persist from/to for these so the preset always uses "current" dates.
 */
const RELATIVE_DATE_PRESETS = new Set([
  'today', 'yesterday', 'overdue', 'last7days', 'lastMonth', 'last3Months', 'last6Months', 'lastYear',
  'thisWeek', 'thisMonth', 'thisYear', 'currentWeek', 'currentMonth', 'currentYear',
  'nextWeek', 'next7days', 'next15days', 'next30days', 'next90days', 'nextMonth', 'next3Months', 'next6Months', 'nextYear',
]);

/**
 * Normalizes unified filters for persistence (save to API).
 * For relative date presets (overdue, thisMonth, etc.), strips from/to so only the
 * preset name is saved. When the preset is applied later, dates are resolved at
 * that time (e.g. "Overdue" always means "before today", not the date at save time).
 */
export function normalizeFiltersForPersistence(
  unifiedFilters: Record<string, UnifiedFilter>
): Record<string, UnifiedFilter> {
  const result: Record<string, UnifiedFilter> = {};
  for (const [key, filter] of Object.entries(unifiedFilters)) {
    if (filter.type === 'dateRange' && filter.preset && filter.preset !== 'all') {
      if (filter.preset === 'custom' && (filter.from || filter.to)) {
        // Custom range: keep from/to as fixed dates
        result[key] = { ...filter };
      } else if (RELATIVE_DATE_PRESETS.has(filter.preset)) {
        // Relative preset: save only preset so it resolves to current dates on apply
        result[key] = { type: 'dateRange', preset: filter.preset };
      } else {
        result[key] = { ...filter };
      }
    } else {
      result[key] = { ...filter };
    }
  }
  return result;
}

